import {
  migrateRecipeSteps,
  RECIPE_STEP_VOCABULARY,
  RETIRED_STEP_RENAMES,
  scanRecipeVocabulary,
  type RecipeVocabularyFinding,
} from '@ctcells/core'

/**
 * 「값 직접 설정」 편집기가 고르게 하는 시퀀스 이름 — **현행 어휘만**(`RECIPE_STEP_VOCABULARY`).
 * 🔴 은퇴 이름(`pick`·`place`·`move_gantry_z`)은 목록에 없다. 적어 넣으면 편집기가 「만들기」를 막는다.
 */
export const STEP_SEQUENCE_OPTIONS = RECIPE_STEP_VOCABULARY

/** 은퇴 이름 → 새 이름(절 구분 없이 펼친 것). 막을 때 「무엇을 쓰라」를 말하는 데만 쓴다 */
export const RETIRED_SEQUENCE_NAMES: Readonly<Record<string, string>> = Object.assign(
  {},
  ...Object.values(RETIRED_STEP_RENAMES),
) as Record<string, string>

/**
 * 레시피 본문의 스텝 어휘를 **표와 대조한** 결과를 화면 문구 재료로 접는다.
 *
 * 🔴 **판정이 아니라 표 대조다**(`@ctcells/core` `recipeStepVocabulary.ts`). 「돌 것인가」는 서버가
 *    정한다 — 이 값으로 버튼을 막지 않는다. 사람에게 «먼저» 알려 줄 뿐이다.
 * ⚠ 은퇴 절(`collaborative_6axis`)은 여기서 말하지 않는다 — 코어가 그 절을 걷지 않아(스키마에 없다)
 *   실행에 닿지 않는다. 경고로 올리면 고칠 수 없는 소음이 된다.
 */
export interface RecipeVocabularyNote {
  /** 은퇴 이름 → 새 이름. 예 `centrifuge.pick → pickup ×20` */
  retired: string[]
  /** 표 어디에도 없는 이름. 예 `staining.foo ×1` */
  unknown: string[]
}

function lineOf(finding: RecipeVocabularyFinding): string | undefined {
  switch (finding.kind) {
    case 'retired':
      return `${finding.section}.${finding.sequence} → ${finding.replacement} ×${String(finding.count)}`
    case 'retiredValue':
      return `${finding.section}.${finding.sequence} ${finding.param}=${finding.value} → ${finding.replacement} ×${String(finding.count)}`
    default:
      return undefined
  }
}

/** 본문 → 안내 재료. 알릴 것이 없으면 `undefined` */
export function recipeVocabularyNote(payload: unknown): RecipeVocabularyNote | undefined {
  const findings = scanRecipeVocabulary(payload)
  const retired = findings.map(lineOf).filter((line): line is string => line !== undefined)
  const unknown = findings.flatMap((f) =>
    f.kind === 'unknown' ? [`${f.section}.${f.sequence} ×${String(f.count)}`] : [],
  )
  return retired.length === 0 && unknown.length === 0 ? undefined : { retired, unknown }
}

/**
 * 새 레시피로 올릴 본문 — 은퇴 이름을 새 이름으로 바꾼 것. 🔴 **그 외 키는 그대로다**(`migrateRecipeSteps`).
 * 복제·JSON 가져오기 둘 다 여기를 지난다 — 옛 레시피를 복제해도 **새 어휘로만** 만들어진다.
 */
export function withCurrentVocabulary<T>(payload: T): { payload: T; changedCount: number } {
  const { payload: next, changed } = migrateRecipeSteps(payload)
  return { payload: next, changedCount: changed.length }
}
